import React, { useState } from 'react';
import { Box, Button, Card, CardContent, Grid, TextField, Typography, MenuItem, Checkbox, FormControlLabel, Divider, InputAdornment, IconButton, Grow, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Lock, Email, Business, Visibility, VisibilityOff, Person, ArrowBack, CheckCircle } from '@mui/icons-material';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { parsePhoneNumberWithError, isValidPhoneNumber } from 'libphonenumber-js';
import API, { NODE_ENV } from 'api';
import ShowTermsAndConditions from 'Common/ShowTermsAndConditions';
import { getEnterpriseRecaptchaToken } from 'Utils/getRecaptchaToken';
import { toast } from 'react-toastify';
import { PhoneInputField } from 'JsUtils/FormikControls';
import styles from "JsUtils/FormikControls/index.module.scss"
import useClientTypes from 'Hooks/useClientType';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import InsightsIcon from '@mui/icons-material/Insights';
import ShowPrivacyPolicy from 'Common/ShowPrivacyPolicy';
import { useNavigation } from 'Hooks/navigate';
import StyledToolTip from 'Common/CustomTooltip';

const RegForm = () => {
    const { goBack, goToSignIn } = useNavigation()
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [opentermsModal, setopentermsModal] = useState(false)
    const [privacyPolicyModalOpen, setPrivacyPolicyModalOpen] = useState(false)
    const [phoneInput, setPhoneInput] = useState('')
    const [phoneCountry, setphoneCountry] = useState('ca')
    const disablePricing = true; // Set to true to disable pricing page access

    // Email validation state
    const [emailValidation, setEmailValidation] = useState({
        isValid: null,
        errors: []
    });
    const [debounceTimer, setDebounceTimer] = useState(null);

    // Resend verification modal state
    const [resendModalOpen, setResendModalOpen] = useState(false);
    const [resendEmail, setResendEmail] = useState('');
    const [resendLoading, setResendLoading] = useState(false);
    const [resendSuccess, setResendSuccess] = useState(false);
    const [resendError, setResendError] = useState('');

    const { clientTypes } = useClientTypes()

    const initialValues = {
        businessName: '',
        firstName: '',
        lastName: '',
        email: '',
        cellNumber: '',
        cellDialCode: '',
        countryCode: 'ca',
        country: '',
        clientType: '',
        password: '',
        confirmPassword: '',
        isAuthAdmin: false,
        isTrial: false,
    }

    // YUP VALIDATION SCHEMA
    const validationSchema = Yup.object({
        businessName: Yup.string()
            .trim()
            .min(2, 'Business Name must be at least 2 characters')
            .max(100, 'Business Name must not exceed 100 characters')
            .matches(
                /^[a-zA-Z0-9\s&+\-,'.]+$/,
                'Business Name can only contain letters, numbers, spaces, and the following special characters: & + - , \''
            )
            .required('Business Name is Required'),
        firstName: Yup.string()
            .trim()
            .min(2, 'First Name must be at least 2 characters')
            .max(50, 'First Name must not exceed 50 characters')
            .matches(/^[a-zA-Z\s'-]+$/, 'First Name can only contain letters, spaces, hyphens, and apostrophes')
            .required('First Name is Required'),
        lastName: Yup.string()
            .trim()
            .min(2, 'Last Name must be at least 2 characters')
            .max(50, 'Last Name must not exceed 50 characters')
            .matches(/^[a-zA-Z\s'-]+$/, 'Last Name can only contain letters, spaces, hyphens, and apostrophes')
            .required('Last Name is Required'),
        email: Yup.string()
            .trim()
            .lowercase()
            .email('Invalid email format')
            .max(100, 'Email must not exceed 100 characters')
            .matches(
                /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                'Please enter a valid email address'
            )
            .required('Email is Required'),
        cellNumber: Yup.string()
            .required('Cell Number is Required')
            .test('is-valid-phone', 'Invalid phone number for selected country', function (value) {
                const { cellDialCode, countryCode } = this.parent;

                if (!value || !cellDialCode) {
                    return false;
                }

                try {
                    // Construct full phone number with dial code
                    const fullPhoneNumber = `+${cellDialCode}${value}`;

                    // Convert country code to uppercase (e.g., 'ca' -> 'CA')
                    const countryCodeUpper = countryCode ? countryCode.toUpperCase() : undefined;

                    // First validate if the phone number is valid in general
                    const isValid = isValidPhoneNumber(fullPhoneNumber, countryCodeUpper);

                    if (!isValid) {
                        return false;
                    }

                    // Additional validation: parse and verify the country matches
                    // Only if a country code is explicitly provided
                    if (countryCodeUpper) {
                        const phoneNumber = parsePhoneNumberWithError(fullPhoneNumber);

                        // Check if the parsed number's country matches the selected country
                        // This ensures consistency between the selected country and the actual number
                        if (phoneNumber.country && phoneNumber.country !== countryCodeUpper) {
                            return this.createError({
                                message: `This number belongs to ${phoneNumber.country}, but you selected ${countryCodeUpper}`
                            });
                        }
                    }

                    return true;
                } catch (error) {
                    console.error('Phone validation error:', error);
                    // If parsing fails, fall back to basic validation
                    return false;
                }
            }),
        clientType: Yup.string()
            .required('Client Type is Required'),
        password: Yup.string()
            .min(8, 'Password must be at least 8 characters')
            .max(12, 'Password must not exceed 12 characters')
            .matches(
                /^(?=.*[!@#$%^&*()_+=\[\]{};':"\\|,.<>/?].*[!@#$%^&*()_+=\[\]{};':"\\|,.<>/?])[a-zA-Z0-9!@#$%^&*()_+=\[\]{};':"\\|,.<>/?]*$/,
                'Password must contain at least 2 special characters and can include letters, numbers, and special characters'
            )
            .required('Password is Required'),
        confirmPassword: Yup.string()
            .oneOf([Yup.ref('password'), null], 'Passwords must match')
            .required('Confirm Password is Required'),
    })

    // Email validation function with API call
    const validateEmailWithAPI = async (email) => {
        if (!email || email.trim() === '') {
            setEmailValidation({
                isValid: null,
                errors: []
            });
            return;
        }

        try {
            const response = await API.emailValidation.validateEmail({ email });
            setEmailValidation({
                isValid: response.data.valid,
                errors: response.data.errors || []
            });
        } catch (error) {
            console.error('Email validation error:', error);
            setEmailValidation({
                isValid: false,
                errors: ['Unable to validate email. Please check your connection.']
            });
        }
    };

    // Handle email change with debouncing
    const handleEmailChange = (e, setFieldValue) => {
        const email = e.target.value;
        setFieldValue('email', email);

        // Clear existing timer
        if (debounceTimer) {
            clearTimeout(debounceTimer);
        }

        // Set new timer for debounced validation
        const timer = setTimeout(() => {
            validateEmailWithAPI(email);
        }, 800);

        setDebounceTimer(timer);
    };

    const handleSubmit = async (values) => {
        // Check if email is valid before submission
        if (emailValidation.isValid === false) {
            toast.error('Please provide a valid business email address.');
            return;
        }

        try {
            const token = await getEnterpriseRecaptchaToken('register');
            const payload = {
                businessName: values.businessName,
                firstName: values.firstName,
                lastName: values.lastName,
                email: values.email,
                cellNumber: `${values.cellDialCode}${values.cellNumber}`,
                cellNumberCountry: values.countryCode,
                clientType: values.clientType,
                password: values.password,
                isAuthAdmin: values.isAuthAdmin,
                recaptchaToken: token,
                recaptchaAction: 'register',
                isTrial: values.isTrial,
            };

            await API.client.create(payload);

            toast.success('Client registered successfully!');
            goToSignIn("client")
        } catch (error) {
            console.error('❌ Registration error caught:', error);
        }
    };

    // Handle resend verification modal
    const handleOpenResendModal = () => {
        setResendModalOpen(true);
        setResendEmail('');
        setResendSuccess(false);
        setResendError('');
    };

    const handleCloseResendModal = () => {
        setResendModalOpen(false);
        setResendEmail('');
        setResendSuccess(false);
        setResendError('');
    };

    const handleResendVerification = async () => {
        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!resendEmail || !emailRegex.test(resendEmail)) {
            setResendError('Please enter a valid email address');
            return;
        }

        setResendLoading(true);
        setResendError('');

        try {
            const response = await API.auth.resendVerificationEmail({
                email: resendEmail,
                userType: 'client'
            });

            if (response.data.success) {
                setResendSuccess(true);
                // Auto-close modal after 2 seconds
                setTimeout(() => {
                    handleCloseResendModal();
                }, 5000);
            } else {
                setResendError(response.data.message || 'Failed to send verification email');
            }
        } catch (error) {
            console.error('Resend verification error:', error);
            const errorMessage = error.response?.data?.message || 'Failed to send verification email';
            setResendError(errorMessage);
        } finally {
            setResendLoading(false);
        }
    };

    const HandleTermsModalOpen = () => {
        setopentermsModal(true)
    }

    const HandlePrivacyPolicyModalOpen = () => {
        setPrivacyPolicyModalOpen(true)
    }

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            validateOnChange={true}
            validateOnBlur={true}
        >
            {({ setFieldValue, handleBlur, errors, touched }) => (
                <Form>
                    <Grow in timeout={1000}>
                        <Card sx={{ height: '100%', width: "80%", m: "auto", borderRadius: 4, p: 1 }}>
                            <CardContent>
                                <Typography variant="h4" fontWeight="bold" textAlign="center" gutterBottom>
                                    Family Information
                                </Typography>
                                <Typography variant="subtitle1" textAlign="center" color="text.secondary" gutterBottom>
                                    Trusted Advisory Service
                                </Typography>

                                <Box mt={3}>
                                    <Typography variant="h6" gutterBottom>
                                        Business Information
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label={
                                                    <>
                                                        Business Name <span style={{ color: 'red' }}>*</span>
                                                    </>
                                                }
                                                InputProps={{ startAdornment: <Business sx={{ mr: 1 }} /> }}
                                                name="businessName"
                                                type="text"
                                                onChange={(e) => setFieldValue('businessName', e.target.value)}
                                                onBlur={handleBlur}
                                            />
                                            {touched.businessName && errors.businessName &&
                                                <div className={styles.error}>{errors.businessName}</div>
                                            }
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                select
                                                label={
                                                    <>
                                                        Client Type <span style={{ color: 'red' }}>*</span>
                                                    </>
                                                }
                                                name="clientType"
                                                onChange={(e) => setFieldValue('clientType', e.target.value)}
                                                onBlur={handleBlur}
                                            >
                                                <MenuItem value="">Select Client Type</MenuItem>
                                                {clientTypes?.map((element) => (
                                                    <MenuItem key={element.id} value={element.type}>{element.type}</MenuItem>
                                                )) || []}
                                            </TextField>
                                            {touched.clientType && errors.clientType &&
                                                <div className={styles.error}>{errors.clientType}</div>
                                            }
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                name="email"
                                                label={
                                                    <>
                                                        Business Email <span style={{ color: 'red' }}>*</span>
                                                    </>
                                                }
                                                type="email"
                                                InputProps={{ startAdornment: <Email sx={{ mr: 1 }} /> }}
                                                onChange={(e) => handleEmailChange(e, setFieldValue)}
                                                onBlur={handleBlur}
                                            />
                                            {touched.email && errors.email &&
                                                <div className={styles.error}>{errors.email}</div>
                                            }
                                            {emailValidation.isValid === false && emailValidation.errors.length > 0 && (
                                                <div className={styles.error}>
                                                    {emailValidation.errors.map((error, index) => (
                                                        <div key={index}>{error}</div>
                                                    ))}
                                                </div>
                                            )}
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <PhoneInputField
                                                placeholder="Enter Business Cell Number"
                                                label="Phone"
                                                showAsterik={true}
                                                required
                                                name='cellNumber'
                                                country={phoneCountry}
                                                value={phoneInput}
                                                onChange={(phone, data, event, formattedValue) => {
                                                    let temp_phone = phone.toString();
                                                    let temp_dialCode = data.dialCode.toString();
                                                    let actualPhone = '';

                                                    if (temp_phone.startsWith(temp_dialCode)) {
                                                        actualPhone = temp_phone.slice(temp_dialCode.length);
                                                    } else {
                                                        actualPhone = temp_phone;
                                                    }

                                                    // Update phone country state to sync with auto-detected country
                                                    setphoneCountry(data.countryCode);

                                                    // Update phone input - this preserves the full number
                                                    setPhoneInput(phone)
                                                    setFieldValue('cellNumber', actualPhone)
                                                    setFieldValue('cellDialCode', temp_dialCode)
                                                    setFieldValue('countryCode', data.countryCode)
                                                }}
                                                disableCountryCode={false}
                                                disableCountryGuess={true}
                                                enableSearch={true}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label={
                                                    <>
                                                        First Name <span style={{ color: 'red' }}>*</span>
                                                    </>
                                                }
                                                name='firstName'
                                                InputProps={{ startAdornment: <Person sx={{ mr: 1 }} /> }}
                                                onChange={(e) => setFieldValue('firstName', e.target.value)}
                                                onBlur={handleBlur}
                                            />
                                            {touched.firstName && errors.firstName &&
                                                <div className={styles.error}>{errors.firstName}</div>
                                            }
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                name='lastName'
                                                label={
                                                    <>
                                                        Last Name <span style={{ color: 'red' }}>*</span>
                                                    </>
                                                }
                                                InputProps={{ startAdornment: <Person sx={{ mr: 1 }} /> }}
                                                onChange={(e) => setFieldValue('lastName', e.target.value)}
                                                onBlur={handleBlur}
                                            />
                                            {touched.lastName && errors.lastName &&
                                                <div className={styles.error}>{errors.lastName}</div>
                                            }
                                        </Grid>
                                    </Grid>
                                </Box>

                                <Divider sx={{ mt: 3, mb: 2 }} />

                                <Box>
                                    <Typography variant="h6" gutterBottom>
                                        Account Setup
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label={
                                                    <>
                                                        Password <span style={{ color: 'red' }}>*</span>
                                                    </>
                                                }
                                                name='password'
                                                type={showPassword ? "text" : "password"}
                                                onChange={(e) => setFieldValue('password', e.target.value)}
                                                onBlur={handleBlur}
                                                InputProps={{
                                                    startAdornment: <Lock sx={{ mr: 1 }} />,
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton onClick={() => setShowPassword(prev => !prev)}>
                                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    )
                                                }}
                                            />
                                            {touched.password && errors.password &&
                                                <div className={styles.error}>{errors.password}</div>
                                            }
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label={
                                                    <>
                                                        Confirm Password <span style={{ color: 'red' }}>*</span>
                                                    </>
                                                }
                                                type={showConfirmPassword ? "text" : "password"}
                                                name="confirmPassword"
                                                onChange={(e) => setFieldValue('confirmPassword', e.target.value)}
                                                onBlur={handleBlur}
                                                InputProps={{
                                                    startAdornment: <Lock sx={{ mr: 1 }} />,
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton onClick={() => setShowConfirmPassword(prev => !prev)}>
                                                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    )
                                                }}
                                            />
                                            {touched.confirmPassword && errors.confirmPassword &&
                                                <div className={styles.error}>{errors.confirmPassword}</div>
                                            }
                                        </Grid>
                                    </Grid>
                                    <Box sx={{ mt: 2 }}>
                                        <FormControlLabel
                                            control={<Checkbox name='isAuthAdmin' />}
                                            label="As an organizational Administrator you are responsible for managing the organization subscription, payment, and each organizational registered user"
                                            onChange={(e) => setFieldValue('isAuthAdmin', e.target.checked)}
                                            name='isAuthAdmin'
                                        />
                                    </Box>
                                </Box>

                                <Box mt={3} display="flex" justifyContent="space-between">
                                    <Button startIcon={<ArrowBack />} variant="outlined" onClick={() => goBack()}>Back</Button>
                                    <Box sx={{ display: "flex", gap: 2 }}>
                                        <StyledToolTip title="Enjoy a 30-day free trial" placement="top">
                                            <Button
                                                startIcon={<InsightsIcon />}
                                                variant="outlined"
                                                onClick={() => setFieldValue(`isTrial`, true)}
                                                type='submit'
                                                disabled={emailValidation.isValid === false}
                                            >
                                                REGISTER FOR FREE TRIAL
                                            </Button>
                                        </StyledToolTip>

                                        {NODE_ENV !== 'production' && <Button
                                            startIcon={<HowToRegIcon />}
                                            variant="contained"
                                            sx={{ backgroundColor: '#2c5364' }}
                                            onClick={() => setFieldValue(`isTrial`, false)}
                                            type='submit'
                                            disabled={emailValidation.isValid === false || disablePricing}
                                        >
                                            Register Family
                                        </Button>
                                        }
                                    </Box>
                                </Box>

                                <Box mt={2} textAlign="center">
                                    <Box variant="body2">
                                        {`By proceeding you agree to the `}
                                        <span onClick={HandleTermsModalOpen} style={{ color: '#1976d2', cursor: 'pointer' }}>
                                            Terms and Conditions
                                        </span>
                                        {` and `}
                                        <span onClick={HandlePrivacyPolicyModalOpen} style={{ color: '#1976d2', cursor: 'pointer' }}>
                                            Privacy Policy
                                        </span>
                                    </Box>
                                    <Typography variant="body2">
                                        Already have an account? <span style={{ color: "#1976d2", cursor: "pointer" }} onClick={() => goToSignIn('client')}>Sign-in</span>
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        Already registered but haven't verified your email?{' '}
                                        <span
                                            style={{ color: "#1976d2", cursor: "pointer", fontWeight: 500 }}
                                            onClick={handleOpenResendModal}
                                        >
                                            Resend verification email
                                        </span>
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grow>

                    {/* Terms and Privacy Modals */}
                    {opentermsModal && (
                        <ShowTermsAndConditions title={"Terms and Conditions"} openModal={opentermsModal} handleClose={() => setopentermsModal(false)} />
                    )}
                    {privacyPolicyModalOpen && (
                        <ShowPrivacyPolicy title={"Privacy Policy"} openModal={privacyPolicyModalOpen} handleClose={() => setPrivacyPolicyModalOpen(false)} />
                    )}

                    {/* Resend Verification Email Modal */}
                    <Dialog
                        open={resendModalOpen}
                        onClose={handleCloseResendModal}
                        maxWidth="sm"
                        fullWidth
                    >
                        {!resendSuccess ? (
                            <>
                                <DialogTitle>Resend Verification Email</DialogTitle>
                                <DialogContent>
                                    <Box sx={{ mt: 2 }}>
                                        <TextField
                                            fullWidth
                                            label="Email Address"
                                            type="email"
                                            value={resendEmail}
                                            onChange={(e) => setResendEmail(e.target.value)}
                                            placeholder="Enter your registered email"
                                            InputProps={{
                                                startAdornment: <Email sx={{ mr: 1, color: 'action.active' }} />
                                            }}
                                            error={!!resendError}
                                            helperText={resendError}
                                        />
                                    </Box>
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={handleCloseResendModal} disabled={resendLoading}>
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={handleResendVerification}
                                        variant="contained"
                                        disabled={resendLoading}
                                        sx={{ backgroundColor: '#2c5364' }}
                                    >
                                        {resendLoading ? 'Sending...' : 'Send Verification'}
                                    </Button>
                                </DialogActions>
                            </>
                        ) : (
                            <>
                                <DialogContent>
                                    <Box sx={{ textAlign: 'center', py: 3 }}>
                                        <CheckCircle sx={{ fontSize: 60, color: '#4caf50', mb: 2 }} />
                                        <Typography variant="h6" gutterBottom>
                                            Success!
                                        </Typography>
                                        <Typography variant="body1" color="text.secondary">
                                            Verification email sent successfully
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                            Please check your inbox
                                        </Typography>
                                    </Box>
                                </DialogContent>
                            </>
                        )}
                    </Dialog>
                </Form>
            )}
        </Formik>
    )
}

export default RegForm